// src/hooks/useUnsavedGuard.js — doced-8f3a61c2 (Document editor, Stage 4: the estimate page)
//
// "You have unsaved changes — leave anyway?" for a full page, not a pop-up.
//
// The app's router is a <BrowserRouter>, not a data router, so react-router's useBlocker isn't
// available. Two ways out of a page are guarded:
//   • closing the tab, reloading, or typing another address — the browser's own "Leave site?"
//     warning (beforeunload), as the estimate pop-up already does;
//   • moving inside the app (the sidebar, a link, a button that navigates) — every one of these goes
//     through window.history.pushState / replaceState, so while the page has unsaved changes those
//     two are wrapped: the person is asked first, and if they choose to stay nothing moves (the router
//     re-reads window.location, which hasn't changed).
// NOT guarded: the browser's own Back / Forward buttons. The router has already moved by the time
// the page could ask, so asking would only lose the work anyway. (Noted in the Stage 4 report.)
//
// guard.allow(fn) runs a navigation the page itself decided on (after a save, or once the person has
// already confirmed) without asking.

import { useEffect, useRef, useMemo } from 'react';

// Navigations the app itself makes (after a save, putting the estimate's number in the address)
// pass every guard. Shared, so a page can move without asking even when the guard is a child's.
let appNavigationDepth = 0;
/** Run fn (which navigates) without any guard asking. */
export function withoutGuard(fn) {
    appNavigationDepth += 1;
    try { return fn(); } finally { appNavigationDepth -= 1; }
}

/**
 * The guard itself, on any window-like object (testable without React).
 * shouldBlock() → true while there are unsaved changes; confirmLeave() → true to leave
 * (default window.confirm); onLeave() runs once the person chose to leave (e.g. discard notes).
 */
export function createHistoryGuard(win, { shouldBlock, message, confirmLeave, onLeave }) {
    const history = win.history;
    const realPush = history.pushState;
    const realReplace = history.replaceState;
    let bypass = 0;
    const ask = confirmLeave || ((m) => win.confirm(m));
    const wrap = (real) => function guarded(...args) {
        if (bypass === 0 && appNavigationDepth === 0 && shouldBlock()) {
            if (!ask(message)) return undefined;
            if (onLeave) onLeave();
        }
        return real.apply(history, args);
    };
    const beforeUnload = (e) => {
        if (!shouldBlock()) return undefined;
        e.preventDefault();
        e.returnValue = '';
        return '';
    };
    const guardedPush = wrap(realPush);
    const guardedReplace = wrap(realReplace);
    history.pushState = guardedPush;
    history.replaceState = guardedReplace;
    win.addEventListener('beforeunload', beforeUnload);
    return {
        allow(fn) {
            bypass += 1;
            try { return fn(); } finally { bypass -= 1; }
        },
        /** Ask now (for the page's own Back/Cancel): → true to go ahead. */
        confirm() {
            if (!shouldBlock()) return true;
            if (!ask(message)) return false;
            if (onLeave) onLeave();
            return true;
        },
        uninstall() {
            // Only put back what is still ours (never undo someone else's wrapper).
            if (history.pushState === guardedPush) history.pushState = realPush;
            if (history.replaceState === guardedReplace) history.replaceState = realReplace;
            win.removeEventListener('beforeunload', beforeUnload);
        },
    };
}

/** React wrapper. when = true while there are unsaved changes. */
export default function useUnsavedGuard({ when, message, onLeave }) {
    const whenRef = useRef(when);
    const onLeaveRef = useRef(onLeave);
    whenRef.current = when;
    onLeaveRef.current = onLeave;
    const guardRef = useRef(null);
    useEffect(() => {
        const g = createHistoryGuard(window, {
            shouldBlock: () => !!whenRef.current,
            message,
            onLeave: () => { if (onLeaveRef.current) onLeaveRef.current(); },
        });
        guardRef.current = g;
        return () => { g.uninstall(); guardRef.current = null; };
    }, [message]);
    return useMemo(() => ({
        allow: (fn) => (guardRef.current ? guardRef.current.allow(fn) : fn()),
        confirm: () => (guardRef.current ? guardRef.current.confirm() : true),
    }), []);
}
